import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ARM_FALLBACK_MS, hasStageArrived, isStageInView } from './story-arming';
import { STAGE } from './story-data';

import type { RefObject } from 'react';

/**
 * `useLayoutEffect` on the client, `useEffect` on the server.
 *
 * The site is prerendered (SSG), and React warns that a layout effect cannot
 * run during `renderToString`. The warning is correct and the fix is not to
 * silence it: on the server there is no layout to measure, so the effect has
 * nothing to do — it must simply not be a LAYOUT effect there. On the client it
 * has to stay one, because measuring in a passive effect shows one frame of the
 * stage at 1× before it scales down, which on a phone is a visible 2.5× jump.
 */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Drives the story stage: fit it to the available width, measure what the
 * choreography interpolates towards, then arm it when the visitor can see it.
 *
 * ## Why measure at all
 *
 * Three of the build's effects cannot be expressed in CSS alone:
 *
 *   - typing (`sxTypeW`) animates `width` to the text's natural width, and
 *     `width: auto` is not an animatable target;
 *   - stroke drawing (`sxDraw`) needs each path's real length as its
 *     `stroke-dasharray`, and paths differ;
 *   - the widget's feed scroll needs each turn's laid-out offset.
 *
 * All three are read ONCE, while the build is still frozen, so the measuring
 * pass never competes with the animation for layout.
 *
 * ## Why arming is a scroll gate rather than an autoplay
 *
 * The build is ~21 seconds and tells a story from its first frame. Started on
 * load, it would be over before a visitor scrolled to it — they would arrive at
 * a finished diagram and never learn that it assembled itself, which is the
 * one thing the band exists to show. So it waits until most of the stage is on
 * screen and only then releases the paused subtree.
 *
 * The rule itself lives in `story-arming.ts`, with the measurements that show
 * why the previous one left the demo blank. Two properties matter here: the
 * gate is a coverage RATIO (so it can never demand more than the viewport can
 * give), and it has a bounded FALLBACK (so a visitor who arrives already parked
 * on the band — a reload with restored scroll, an anchor, one Page Down —
 * generates no scroll event and is not left staring at an empty black
 * rectangle forever).
 */

interface StoryStage {
  /** Attach to the scroller that clips the scaled stage. */
  readonly rootRef: RefObject<HTMLDivElement>;
  /** `transform` for the stage: centre it, then scale to fit. */
  readonly transform: string;
  /** The scroller's height once the stage is scaled — it has no natural one. */
  readonly height: string;
  /** `'1'` once the build has been released. Drives the CSS pause switch. */
  readonly armed: '0' | '1';
}

export function useStoryStage(): StoryStage {
  const rootRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState(0);
  const [armed, setArmed] = useState<'0' | '1'>('0');

  // Fit-to-width, before the first paint on the client. See the hook's note.
  useIsomorphicLayoutEffect(() => {
    const fit = (): void => {
      const el = rootRef.current;
      const available = (el?.clientWidth ?? STAGE.w) - 40;
      const next = Math.max(0.4, Math.min(1, available / STAGE.w));
      setScale(next);
      setOffset(Math.max(0, Math.round((available - STAGE.w * next) / 2)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
    };
  }, []);

  // Measure, then watch for the band to scroll in.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    // A visitor who asked for less motion gets the finished stage immediately:
    // the global reduced-motion rule already stripped the animations, so
    // leaving this paused would only mean the pause attribute never clears.
    // Nothing here needs measuring in that case either.
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setArmed('1');
      return undefined;
    }

    const measure = (): void => {
      el.querySelectorAll<HTMLElement>('[data-type]').forEach((node) => {
        node.style.setProperty('--w', `${Math.ceil(node.scrollWidth + 1)}px`);
      });
      // The transcript's ride: one stop per turn, each placing that turn's
      // BOTTOM edge on the feed's bottom edge. Measured rather than stepped by
      // a constant because the turns have different heights — and different
      // heights again in every locale.
      const feed = el.querySelector<HTMLElement>('[data-feed]');
      const viewport = feed?.parentElement;
      if (feed && viewport) {
        const usable = viewport.clientHeight - 32;
        Array.from(feed.children).forEach((turn, i) => {
          const node = turn as HTMLElement;
          const bottom = node.offsetTop - feed.offsetTop + node.offsetHeight;
          feed.style.setProperty(`--f${i}`, `${Math.round(usable - bottom)}px`);
        });
      }

      el.querySelectorAll<SVGPathElement>('[data-line]').forEach((path) => {
        try {
          path.style.setProperty('--len', String(Math.ceil(path.getTotalLength())));
        } catch {
          // jsdom and a detached SVG both throw here. The stroke then keeps
          // its authored fallback length and simply draws at a slightly wrong
          // rate — never a broken frame, which is why this is swallowed.
        }
      });
    };

    const viewport = (): number => window.innerHeight || document.documentElement.clientHeight || 0;

    let observer: IntersectionObserver | undefined;
    let fallback: ReturnType<typeof setTimeout> | undefined;

    const check = (): void => {
      if (!isStageInView(el.getBoundingClientRect(), viewport())) return;
      stop();
      setArmed('1');
    };
    const stop = (): void => {
      observer?.disconnect();
      observer = undefined;
      if (fallback !== undefined) clearTimeout(fallback);
      fallback = undefined;
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };

    // Fonts change every measured width, so measure after they settle. The
    // `.then(arm, arm)` pair means a rejected `fonts.ready` still arms — a
    // font that fails to load must not leave the band frozen forever.
    const arm = (): void => {
      measure();
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(check, { threshold: [0, 0.25, 0.5, 0.75, 1] });
        observer.observe(el);
      }
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check);
      // The weaker second question, asked once. Both the observer and the
      // listener report CHANGES, so a visitor who never scrolls after landing
      // on the band gets exactly one "not yet" and no further chance.
      fallback = setTimeout(() => {
        if (!hasStageArrived(el.getBoundingClientRect(), viewport())) return;
        stop();
        setArmed('1');
      }, ARM_FALLBACK_MS);
      check();
    };

    // The DOM types declare `document.fonts` as always present, so a truthiness
    // guard reads as dead code to the linter — but it is genuinely absent in
    // older browsers and in jsdom. `in` is the runtime check that survives both.
    if ('fonts' in document) {
      // `.then(arm, arm)`: a REJECTED `fonts.ready` must still arm, or a font
      // that fails to load leaves the band frozen forever.
      document.fonts.ready.then(arm, arm);
    } else {
      arm();
    }

    return stop;
  }, []);

  return {
    rootRef,
    transform: `translateX(${offset}px) scale(${scale})`,
    height: `${Math.round(STAGE.h * scale)}px`,
    armed,
  };
}
